import type { AngleAffiche, LectureDeLApp } from '@nextilt/commun';

/**
 * **Les lectures de l'app** (chantier journal-des-gestes, ticket 05) : ce que la feuille montre
 * (le projet, la feuille, la fiche ouverte, l'angle) devient des signaux pour le journal des
 * gestes, une ligne par vrai changement. Une règle sans React ni réseau ; le crochet qui la
 * branche sur la page et sur l'envoi : `useLecturesDeLApp.ts`.
 *
 * Toute fiche s'ouvre dans une feuille (`useAllerALaFiche` y mène, ou l'ouvre sur place) : ce que
 * la feuille montre suffit à suivre les projets, les fiches et les angles.
 */

/** Ce que la feuille montre. */
export interface CeQueMontreLApp {
  /** Le projet : la racine du chemin. */
  projet: string;
  /** La bulle dont la feuille est ouverte (le projet, ou plus bas). */
  feuille: string;
  /** La fiche ouverte, ou aucune. */
  fiche: string | null;
  /** L'angle affiché. */
  angle: AngleAffiche;
}

/**
 * Un signal : la lecture, la bulle visée, l'ancien et le nouvel angle d'un changement d'angle ;
 * et l'angle à joindre à la requête (celui d'après pour une ouverture, celui d'avant pour une
 * fermeture).
 */
export interface SignalDeLecture {
  geste: LectureDeLApp;
  noeudId: string;
  avant?: AngleAffiche;
  apres?: AngleAffiche;
  angle: AngleAffiche;
}

/**
 * Les signaux qui mènent de ce qui était montré à ce qui l'est : la fiche se ferme avant son
 * projet, le projet s'ouvre avant sa fiche. Un autre projet ne « change » pas d'angle : son
 * ouverture porte le sien. Descendre dans le même projet n'est pas une lecture. Rien ne change,
 * rien ne part.
 */
export function signauxEntre(avant: CeQueMontreLApp | null, apres: CeQueMontreLApp | null): SignalDeLecture[] {
  const signaux: SignalDeLecture[] = [];
  const memeProjet = avant !== null && apres !== null && avant.projet === apres.projet;
  if (avant && avant.fiche !== null && (!memeProjet || avant.fiche !== apres?.fiche)) {
    signaux.push({ geste: 'fermer_fiche', noeudId: avant.fiche, angle: avant.angle });
  }
  if (avant && !memeProjet) signaux.push({ geste: 'fermer_projet', noeudId: avant.projet, angle: avant.angle });
  if (apres && !memeProjet) signaux.push({ geste: 'ouvrir_projet', noeudId: apres.projet, angle: apres.angle });
  if (memeProjet && avant.angle !== apres.angle) {
    signaux.push({ geste: 'changer_angle', noeudId: apres.feuille, avant: avant.angle, apres: apres.angle, angle: apres.angle });
  }
  if (apres && apres.fiche !== null && (!memeProjet || avant.fiche !== apres.fiche)) {
    signaux.push({ geste: 'ouvrir_fiche', noeudId: apres.fiche, angle: apres.angle });
  }
  return signaux;
}

/**
 * Le suivi des lectures : ce que le serveur sait ouvert, ce qui est montré, et si l'app est en
 * arrière-plan. Caché, ce qui était ouvert se ferme ; au retour, ce qui est montré se rouvre. Un
 * envoi qui lève n'arrête jamais les suivants.
 */
export function lecturesDeLApp(envoyer: (signal: SignalDeLecture) => void) {
  let ouvert: CeQueMontreLApp | null = null;
  let montre: CeQueMontreLApp | null = null;
  let cache = false;

  const allerVers = (cible: CeQueMontreLApp | null) => {
    for (const signal of signauxEntre(ouvert, cible)) {
      try {
        envoyer(signal);
      } catch {
        // Le journal ne gêne jamais l'écran.
      }
    }
    ouvert = cible;
  };

  return {
    /** Ce que la feuille montre maintenant ; `null` quand elle part. */
    montrer(m: CeQueMontreLApp | null) {
      montre = m;
      if (!cache) allerVers(m);
    },
    /** L'app passe en arrière-plan, ou l'onglet se ferme. */
    cacher() {
      cache = true;
      allerVers(null);
    },
    /** L'app revient au premier plan. */
    revenir() {
      cache = false;
      allerVers(montre);
    },
  };
}
